/**
 * Tenant routing for the main CRM SPA.
 * CCC is a separate app — admins open it via its own /login URL (no JWT handoff).
 */

import {
  getMainCrmAppUrl,
  getTenantLoginUrl as resolveTenantLoginUrl,
  isCccAppHost,
} from '../config/crmAppUrls';
import { clearCrmAuthSession } from './authSession';

export type Tenant =
  | 'callcenter'
  | 'grh'
  | 'comercial'
  | 'backoffice'
  | 'admin'
  | 'partnership'
  | 'main'
  | 'ccc';

export {
  getCccAppUrl,
  getCccAppPath,
  getMainCrmAppUrl,
  getCccLoginUrl,
  getMainCrmLoginUrl,
  isCccAppHost,
} from '../config/crmAppUrls';

const POST_AUTH_TARGET = 'post_auth_target';
const POST_AUTH_PATH = 'post_auth_path';

export const getUserTenant = (): Tenant => {
  try {
    const rawCognito = localStorage.getItem('cognito_user');
    if (rawCognito) {
      const u = JSON.parse(rawCognito);
      if (u?.tenant) return u.tenant as Tenant;
    }
    const rawUser = localStorage.getItem('user');
    if (rawUser) {
      const u = JSON.parse(rawUser);
      if (u?.tenant) return u.tenant as Tenant;
    }
    const rawTenant = localStorage.getItem('user_tenant');
    if (rawTenant) return rawTenant as Tenant;
  } catch {
    /* ignore */
  }
  return 'callcenter';
};

export const getTenantLoginUrl = (tenantId: string): string | null =>
  resolveTenantLoginUrl(tenantId);

/** Clear stored auth before switching to another CRM login. */
export const clearAuthSessionForCrmSwitch = (): void => {
  clearCrmAuthSession();
};

/** Internal SPA path after login (main CRM tenants only). */
export const getMainPagePath = (tenant: Tenant): string => {
  switch (tenant) {
    case 'grh':
      return '/grh/triagem';
    case 'callcenter':
      return '/callcenter';
    case 'comercial':
      return '/comercial/calendar';
    case 'backoffice':
      return '/backoffice';
    case 'admin':
      return '/callcenter';
    case 'partnership':
      return '/partnership/dashboard';
    case 'ccc':
      return '/login';
    case 'main':
    default:
      return '/callcenter';
  }
};

/** @deprecated Use getMainPagePath */
export const getMainPageUrl = (tenant: Tenant): string => getMainPagePath(tenant);

export const stashPostAuthDestination = (tenant: string, path = '/'): void => {
  sessionStorage.setItem(POST_AUTH_TARGET, tenant);
  sessionStorage.setItem(POST_AUTH_PATH, path);
};

export const consumePostAuthDestination = (): { tenant: string; path: string } | null => {
  const tenant = sessionStorage.getItem(POST_AUTH_TARGET);
  if (!tenant) return null;
  const path = sessionStorage.getItem(POST_AUTH_PATH) || '/';
  sessionStorage.removeItem(POST_AUTH_TARGET);
  sessionStorage.removeItem(POST_AUTH_PATH);
  return { tenant, path };
};

/**
 * In-app navigation after login (main CRM tenants only).
 * CCC users must use the dedicated CCC app URL.
 */
export const routeUserToTenant = (
  navigate: (path: string, options?: { replace?: boolean }) => void,
  tenantId: string,
  path = '/'
): void => {
  if (tenantId === 'ccc') {
    const loginUrl = getTenantLoginUrl('ccc');
    if (loginUrl) window.location.replace(loginUrl);
    return;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (tenantId === 'grh') {
    navigate('/grh/triagem', { replace: true });
    return;
  }
  if (tenantId === 'callcenter' || tenantId === 'admin') {
    navigate('/callcenter', { replace: true });
    return;
  }
  if (tenantId === 'comercial') {
    navigate('/comercial/calendar', { replace: true });
    return;
  }
  if (tenantId === 'backoffice') {
    navigate('/backoffice', { replace: true });
    return;
  }
  if (tenantId === 'partnership') {
    navigate('/partnership/dashboard', { replace: true });
    return;
  }

  navigate(normalizedPath || getMainPagePath(getUserTenant()), { replace: true });
};

/** After login on main CRM: honour pending destination, else route by user tenant. */
export const routeUserAfterAuth = (
  navigate: (path: string, options?: { replace?: boolean }) => void
): void => {
  const pending = consumePostAuthDestination();
  if (pending) {
    if (pending.tenant === 'ccc') {
      const loginUrl = getTenantLoginUrl('ccc');
      if (loginUrl) window.location.replace(loginUrl);
      return;
    }
    routeUserToTenant(navigate, pending.tenant, pending.path);
    return;
  }

  const tenant = getUserTenant();
  if (tenant === 'ccc') {
    clearAuthSessionForCrmSwitch();
    const loginUrl = getTenantLoginUrl('ccc');
    if (loginUrl) window.location.replace(loginUrl);
    return;
  }

  navigate(getMainPagePath(tenant), { replace: true });
};

/**
 * Admin tenant pill switch.
 * - Main CRM tenant -> Main CRM tenant: keep session and navigate directly.
 * - Main CRM <-> CCC: clear session and go to target login page.
 */
export const openTenantLoginPage = (tenantId: string): boolean => {
  if (tenantId === 'ccc') {
    const loginUrl = getTenantLoginUrl('ccc');
    if (!loginUrl) return false;
    if (isCccAppHost()) {
      window.location.href = loginUrl;
      return true;
    }
    clearAuthSessionForCrmSwitch();
    window.location.href = loginUrl;
    return true;
  }

  if (['grh', 'callcenter', 'comercial', 'backoffice', 'admin', 'partnership', 'main'].includes(tenantId)) {
    if (isCccAppHost()) {
      clearAuthSessionForCrmSwitch();
      const loginUrl = getTenantLoginUrl(tenantId);
      if (!loginUrl) return false;
      window.location.href = loginUrl;
      return true;
    }
    window.location.href = getMainPagePath(tenantId as Tenant);
    return true;
  }

  return false;
};

export const isPathForTenant = (path: string, tenant: Tenant): boolean => {
  if (tenant === 'partnership') return path.startsWith('/partnership');
  if (tenant === 'grh') return path.startsWith('/grh');
  if (tenant === 'callcenter') return path.startsWith('/callcenter');
  if (tenant === 'comercial') return path.startsWith('/comercial');
  if (tenant === 'backoffice') return path.startsWith('/backoffice');
  if (tenant === 'ccc') return false;
  if (tenant === 'admin') return path.startsWith('/admin');
  return path.startsWith('/callcenter');
};

export const getTenantFromPath = (path: string): Tenant => {
  if (path.startsWith('/partnership')) return 'partnership';
  if (path.startsWith('/grh')) return 'grh';
  if (path.startsWith('/callcenter')) return 'callcenter';
  if (path.startsWith('/comercial')) return 'comercial';
  if (path.startsWith('/backoffice')) return 'backoffice';
  if (path.startsWith('/admin')) return 'admin';
  return 'callcenter';
};
